import { PantallaCargando } from '@/components/Cargando';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BarChart3, Check, Loader2, Lock } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

/**
 * Medición del sitio — Pixel, Analytics, Tag Manager, CAPI.
 *
 * El backend de medición existe desde ADR-157, pero nunca tuvo pantalla propia:
 * se configuraba desde un panel del constructor visual. Al retirar el
 * constructor quedó sin puerta, y desde afuera se veía como si la medición se
 * hubiera perdido. Esta pantalla es esa puerta.
 *
 * Las credenciales (los tokens de CAPI) NUNCA vuelven del servidor: solo llega
 * un aviso de si hay una guardada. Por eso el campo se muestra vacío con la
 * leyenda «ya guardado» en vez de rellenarse con asteriscos falsos.
 */

// Nombres en cristiano y para qué sirve cada uno. El id debe coincidir con el
// catálogo del backend (tracking.py).
const AYUDA = {
  meta_pixel: ['Pixel de Facebook e Instagram',
    'Mide quién visita tu sitio desde tus anuncios de Meta.'],
  meta_capi: ['API de Conversiones de Meta',
    'Manda las conversiones desde el servidor: sigue funcionando aunque el navegador bloquee el pixel.'],
  ga4: ['Google Analytics',
    'Las visitas, de dónde vienen y qué páginas ven.'],
  gtm: ['Google Tag Manager',
    'Un contenedor para administrar todas tus etiquetas sin tocar el código.'],
  google_ads: ['Google Ads', 'Mide las conversiones de tus anuncios de Google.'],
  tiktok_pixel: ['Pixel de TikTok', 'Mide las visitas que llegan desde TikTok.'],
  tiktok_events_api: ['API de Eventos de TikTok', 'La versión desde el servidor del pixel de TikTok.'],
  linkedin_insight: ['LinkedIn Insight', 'Mide las visitas que llegan desde LinkedIn.'],
  linkedin_conversion: ['Conversiones de LinkedIn', 'Registra conversiones en LinkedIn.'],
  x_pixel: ['Pixel de X', 'Mide las visitas que llegan desde X (Twitter).'],
  microsoft_clarity: ['Microsoft Clarity', 'Grabaciones y mapas de calor de cómo usan tu sitio.'],
  pinterest_tag: ['Etiqueta de Pinterest', 'Mide las visitas que llegan desde Pinterest.'],
};

const ETIQUETA_CAMPO = {
  pixel_id: 'ID del pixel',
  measurement_id: 'ID de medición (empieza con G-)',
  container_id: 'ID del contenedor (empieza con GTM-)',
  conversion_id: 'ID de conversión',
  conversion_label: 'Etiqueta de conversión',
  partner_id: 'ID de socio',
  project_id: 'ID del proyecto',
  tag_id: 'ID de la etiqueta',
  access_token: 'Token de acceso',
};

// Los que cualquier plan puede usar; el resto exige el add-on.
const BASICOS = new Set(['ga4', 'meta_pixel', 'gtm']);

function Herramienta({ prov, guardado, bloqueado, onGuardar }) {
  const [nombre, desc] = AYUDA[prov.id] || [prov.label, ''];
  const [valores, setValores] = useState({});
  const [secretos, setSecretos] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setValores({ ...(guardado?.public_config || {}) });
    setSecretos({});
  }, [guardado]);

  const activo = !!guardado?.enabled;

  const guardar = async (encender) => {
    setGuardando(true);
    try {
      await onGuardar({
        provider: prov.id,
        public_config: valores,
        credentials: Object.keys(secretos).length ? secretos : null,
        consent_category: prov.consent,
        enabled: encender,
      });
      setSecretos({});
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="border border-[#E2E8F0] rounded-xl bg-white p-4 space-y-3"
         data-testid={`web-tracking-${prov.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-[#0F172A] flex items-center gap-2">
            {nombre}
            {activo && (
              <span className="text-[10px] uppercase font-bold text-emerald-700
                               bg-emerald-50 px-1.5 py-0.5 rounded">Activo</span>
            )}
          </p>
          <p className="text-xs text-[#64748B] mt-0.5">{desc}</p>
        </div>
        {bloqueado && (
          <span className="text-xs text-[#94A3B8] flex items-center gap-1 shrink-0">
            <Lock className="w-3.5 h-3.5" /> No incluido en tu plan
          </span>
        )}
      </div>

      {!bloqueado && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            {(prov.public_fields || []).map((campo) => (
              <div key={campo}>
                <label htmlFor={`${prov.id}-${campo}`} className="text-xs text-[#475569]">
                  {ETIQUETA_CAMPO[campo] || campo}
                </label>
                <Input id={`${prov.id}-${campo}`} className="mt-1 font-mono text-sm"
                       value={valores[campo] || ''}
                       onChange={(e) => setValores({ ...valores, [campo]: e.target.value })} />
              </div>
            ))}
            {(prov.secret_fields || []).map((campo) => (
              <div key={campo}>
                <label htmlFor={`${prov.id}-${campo}`} className="text-xs text-[#475569]">
                  {ETIQUETA_CAMPO[campo] || campo}
                </label>
                <Input id={`${prov.id}-${campo}`} type="password" className="mt-1 font-mono text-sm"
                       placeholder={guardado?.has_credentials ? 'Ya guardado — escribe para cambiarlo' : ''}
                       value={secretos[campo] || ''}
                       onChange={(e) => setSecretos({ ...secretos, [campo]: e.target.value })} />
                <p className="text-[11px] text-[#94A3B8] mt-0.5">
                  Se guarda cifrado y nunca vuelve a mostrarse.
                </p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => guardar(true)} disabled={guardando}
                    className="bg-[#F72585] hover:bg-[#D81B70]"
                    data-testid={`web-tracking-save-${prov.id}`}>
              {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                         : <Check className="w-4 h-4 mr-1.5" />}
              {activo ? 'Guardar cambios' : 'Activar'}
            </Button>
            {activo && (
              <Button size="sm" variant="ghost" onClick={() => guardar(false)} disabled={guardando}>
                Desactivar
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function WebTracking() {
  const { siteId } = useParams();
  const [cat, setCat] = useState([]);
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [c, e] = await Promise.all([
        api.get('/tenant/web/tracking/providers'),
        api.get(`/tenant/web/sites/${siteId}/tracking`),
      ]);
      setCat(c.data?.providers || []);
      setEstado(e.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, [siteId]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (payload) => {
    try {
      await api.put(`/tenant/web/sites/${siteId}/tracking`, payload);
      toast.success(payload.enabled ? 'Herramienta activada' : 'Herramienta desactivada');
      cargar();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      throw err;
    }
  };

  if (cargando) return <PantallaCargando texto="Cargando la medición…" />;

  const porProveedor = Object.fromEntries(
    (estado?.settings || []).map((s) => [s.provider, s]));
  const avanzado = !!estado?.advanced_enabled;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="web-tracking">
      <Link to={`/tenant/web/sites/${siteId}`} className="text-xs text-[#F72585]">← Volver al sitio</Link>

      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-[#F72585]" /> Medición
        </h1>
        <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
          Conecta tus herramientas de medición y se insertan solas en el sitio al
          publicar. No tienes que pegar código en tu HTML.
        </p>
      </div>

      {estado && !estado.enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          La medición todavía no está activa en la plataforma. Escríbenos y la
          habilitamos; lo que configures aquí se guarda mientras tanto.
        </div>
      )}

      <div className="space-y-3">
        {cat.map((prov) => (
          <Herramienta
            key={prov.id}
            prov={prov}
            guardado={porProveedor[prov.id]}
            bloqueado={!BASICOS.has(prov.id) && !avanzado}
            onGuardar={guardar}
          />
        ))}
      </div>

      <p className="text-xs text-[#94A3B8]">
        Los cambios se aplican la próxima vez que publiques el sitio.
      </p>
    </div>
  );
}
